import React, { useState, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { footerData, contactInfo } from '../data/mock';
import { Send, ArrowUpRight, Mail, Phone, MapPin, Sparkles } from 'lucide-react';
import { Input } from './ui/input';
import { toast } from 'sonner';

const Footer = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      toast.success("Message sent successfully! I'll get back to you soon.");
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleBookCall = () => {
    window.location.href = `mailto:${contactInfo.email}?subject=Free Strategy Call Request&body=Hi Arnab,%0D%0A%0D%0AI would like to book a free strategy call.%0D%0A%0D%0AThank you!`;
  };

  return (
    <footer ref={sectionRef} id="contact" className="relative overflow-hidden border-t border-white/5">
      {/* Parallax Background */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ y }}>
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-gradient-to-tr from-violet-600/20 via-purple-600/10 to-transparent blur-[200px]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gradient-to-bl from-blue-600/15 via-indigo-600/10 to-transparent blur-[150px]" />
      </motion.div>

      {/* Floating elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div 
          className="absolute top-20 right-[15%] w-16 h-16 border border-violet-500/20 rounded-full animate-float-slow"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, -60]) }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Left Side */}
          <div>
            <motion.div className="inline-flex items-center gap-2 glass px-5 py-2.5 mb-6 rounded-full">
              <Sparkles size={16} className="text-violet-400" />
              <span className="text-violet-400 text-sm font-semibold tracking-wider">LET'S CONNECT</span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Book A Free Call<br /><span className="text-gradient-mixed">For Custom Quote</span>
            </h2>
            
            <p className="text-white/50 text-lg mb-10 max-w-md">
              Ready to transform your online presence? Let's discuss your project and find the perfect solution.
            </p>

            <motion.button
              onClick={handleBookCall}
              className="mb-12 bg-gradient-to-r from-violet-600 via-purple-600 to-blue-600 text-white px-8 py-4 text-lg font-medium flex items-center gap-3 overflow-hidden transition-all duration-500 hover:shadow-[0_0_50px_rgba(139,92,246,0.5)] rounded-xl group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Custom Quote
              <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </motion.button>

            {/* Contact Details */}
            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: contactInfo.email, href: `mailto:${contactInfo.email}` },
                { icon: Phone, label: 'Phone', value: contactInfo.phone, href: `tel:${contactInfo.phone.replace(/\s/g, '')}` },
                { icon: MapPin, label: 'Location', value: contactInfo.location }
              ].map((item, index) => (
                <motion.a 
                  key={index}
                  href={item.href || '#'}
                  className="flex items-center gap-4 group"
                  whileHover={{ x: 5 }}
                >
                  <div className="w-14 h-14 glass rounded-2xl flex items-center justify-center group-hover:bg-violet-500/20 transition-colors">
                    <item.icon size={22} className="text-violet-400" />
                  </div>
                  <div>
                    <p className="text-white/40 text-sm">{item.label}</p>
                    <p className="text-white group-hover:text-violet-400 transition-colors">{item.value}</p>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Right Side - Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {['name', 'email'].map((field) => (
                <div key={field}>
                  <label htmlFor={field} className="block text-white/70 text-sm mb-2 capitalize">
                    {field === 'name' ? 'Your Name' : 'Email Address'}
                  </label>
                  <Input
                    id={field}
                    name={field}
                    type={field === 'email' ? 'email' : 'text'}
                    placeholder={field === 'name' ? 'John Doe' : 'john@example.com'}
                    value={formData[field]}
                    onChange={handleChange}
                    required
                    className="w-full glass border-white/10 text-white placeholder:text-white/30 h-14 px-5 rounded-xl focus:border-violet-500 focus:ring-violet-500/20 transition-all hover:border-violet-500/30"
                  />
                </div>
              ))}

              <div>
                <label htmlFor="message" className="block text-white/70 text-sm mb-2">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell me about your project..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full glass border border-white/10 text-white placeholder:text-white/30 px-5 py-4 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 transition-all resize-none outline-none hover:border-violet-500/30"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="group w-full bg-gradient-to-r from-violet-600 via-purple-600 to-blue-600 text-white py-4 text-lg font-medium flex items-center justify-center gap-2 transition-all duration-400 disabled:opacity-70 disabled:cursor-not-allowed hover:shadow-[0_0_40px_rgba(139,92,246,0.5)] rounded-xl"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">{footerData.copyright}</p>
            <div className="flex items-center gap-6">
              {['Privacy Policy', 'Terms of Service'].map((item) => (
                <a key={item} href="#" className="text-white/40 text-sm hover:text-violet-400 transition-colors duration-300 flex items-center gap-1">
                  {item} <ArrowUpRight size={14} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
